import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StateSelector from '../../Components/StateSelector';
import {
  getPortalAccount,
  updatePortalAccount,
} from '../../endpoints/portalAccount';
import {
  PORTAL_US_COUNTRY_ID,
  type PortalAccountContact,
} from '../../types/portalAccount';
import { getApiErrorMessage, useApiGet } from '../../utils/httpClientTs';

const emptyForm = (): PortalAccountContact => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state_id: '',
  state: '',
  postal_code: '',
});

const AccountPortal: React.FC = () => {
  const { data, isLoading, isError, error, refetch } = useApiGet(
    ['portal-account-me'],
    getPortalAccount,
    { retry: 1 }
  );

  const [form, setForm] = useState<PortalAccountContact>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    setForm({
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      email: data.email || '',
      phone: data.phone || '',
      address_line_1: data.address_line_1 || '',
      address_line_2: data.address_line_2 || '',
      city: data.city || '',
      state_id: data.state_id || '',
      state: data.state || '',
      postal_code: data.postal_code || '',
      address_id: data.address_id,
    });
  }, [data]);

  const update =
    (field: keyof PortalAccountContact) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updatePortalAccount(form);
      setForm({
        ...updated,
        first_name: updated.first_name || '',
        last_name: updated.last_name || '',
        email: updated.email || '',
        phone: updated.phone || '',
        address_line_1: updated.address_line_1 || '',
        address_line_2: updated.address_line_2 || '',
        city: updated.city || '',
        state_id: updated.state_id || '',
        state: updated.state || '',
        postal_code: updated.postal_code || '',
      });
      if (updated.first_name) {
        localStorage.setItem('first_name', updated.first_name);
      }
      if (updated.email) {
        localStorage.setItem('email', updated.email);
      }
      setSaved(true);
      await refetch();
    } catch (err) {
      setSaveError(getApiErrorMessage(err) || 'Unable to save contact info.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load your account.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Account
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Update your contact information. Changes apply to your P.E.O. profile.
      </Typography>

      {saveError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSaveError(null)}
        >
          {saveError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={(e) => void handleSave(e)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                fullWidth
                required
                value={form.first_name}
                onChange={update('first_name')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                fullWidth
                required
                value={form.last_name}
                onChange={update('last_name')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={form.email}
                onChange={update('email')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={form.phone}
                onChange={update('phone')}
                disabled={saving}
                helperText="Saved as your mobile / cell phone"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 1"
                fullWidth
                value={form.address_line_1}
                onChange={update('address_line_1')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 2"
                fullWidth
                value={form.address_line_2}
                onChange={update('address_line_2')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="City"
                fullWidth
                value={form.city}
                onChange={update('city')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StateSelector
                countryId={PORTAL_US_COUNTRY_ID}
                value={form.state_id}
                required={false}
                disabled={saving}
                handleChange={(state: { id: string; name: string } | null) => {
                  setForm((prev) => ({
                    ...prev,
                    state_id: state?.id || '',
                    state: state?.name || '',
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postal code"
                fullWidth
                value={form.postal_code}
                onChange={update('postal_code')}
                disabled={saving}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Contact information saved"
      />
    </Box>
  );
};

export default AccountPortal;
